import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouteLocationNormalized,
} from 'vue-router'
import authService from './api/authService'
import { getInstance as getAuth0ServiceIntance } from '@/auth/index'
import {
  isInMaintenance,
  getDomainUnderMaintenance,
} from '@/plan/metadata/metadata.helper'
import { getAppMetadata } from './plan/metadata/metadata'
import {
  StorageDestination,
  setValueToStorage,
  getValueFromStorage,
  removeValueFromStorage,
} from '@/cache/storage.helper'
import { SessionStorageKeys } from './constants'
import SharedPlanPasswordManager from '@/helpers/sharedPlanPassword.helper'

declare module 'vue-router' {
  interface RouteMeta {
    skipAuth?: boolean
    toolbar?: boolean
    miniToolbar?: boolean
  }
}

const Root = () => import('./views/root/Root.vue')
const Login = () => import('./views/login/Login.vue')
const Dashboard = () => import('./views/dashboard/Dashboard.vue')
const Plan = () => import('./views/plan/Plan.vue')
const PanelInsights = () => import('./views/panel-insights/PanelInsights.vue')
const ManagePois = () => import('./views/manage-pois/ManagePois.vue')
const ManagePoisDetails = () =>
  import('./views/manage-pois-details/ManagePoisDetails.vue')
const SharedPlanFeedback = () =>
  import('./views/shared-plan-feedback/SharedPlanFeedback.vue')
const PlanNotFound = () => import('./views/plan-not-found/PlanNotFound.vue')
const SharedPlanPassword = () =>
  import('./views/shared-plan-password/SharedPlanPassword.vue')
const Maintenance = () => import('./views/maintenance/Maintenance.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/shared-plan-feedback',
    name: 'shared-plan-feedback',
    component: SharedPlanFeedback,
    meta: { skipAuth: true },
  },
  {
    path: '/plan-not-found',
    name: 'plan-not-found',
    component: PlanNotFound,
    meta: { skipAuth: true },
  },
  {
    path: '/shared-plan-password',
    name: 'shared-plan-password',
    component: SharedPlanPassword,
    meta: { skipAuth: true },
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: Maintenance,
    meta: { skipAuth: true },
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: { skipAuth: true },
  },
  {
    path: '/',
    component: Root,
    children: [
      {
        path: '',
        name: 'dashboard',
        component: Dashboard,
      },
      {
        path: 'pub',
        name: 'public',
        props: true,
        component: Plan,
        meta: { toolbar: true },
      },
      {
        path: 'plan/:id?',
        name: 'plan',
        props: true,
        component: Plan,
        meta: { toolbar: true },
      },
      {
        path: 'insights/:type/:id',
        name: 'panel-insights',
        component: PanelInsights,
        props: true,
        meta: { toolbar: true, miniToolbar: true },
      },
      {
        path: 'insights',
        name: 'share-insights',
        component: PanelInsights,
        props: false,
        meta: { toolbar: true },
      },
      {
        path: 'plan',
        name: 'share-plan',
        component: Plan,
        props: true,
        meta: { toolbar: true },
      },
      {
        path: 'manage-pois',
        name: 'manage-pois',
        component: ManagePois,
      },
      {
        path: 'manage-pois/:id',
        name: 'manage-pois-details',
        component: ManagePoisDetails,
        // `name`/`description` travel as query params: Vue Router 4 drops route
        // params that are not part of the path (`:id`), so they must not be passed
        // via `params`. Map id (path) + name/description (query) onto the props.
        props: (route) => ({
          id: route.params.id,
          name: route.query.name,
          description: route.query.description,
        }),
        meta: { toolbar: true, miniToolbar: true },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

const redirectToMaintenance = (to: RouteLocationNormalized): void => {
  const appMetadata = getAppMetadata()
  const needsRedirect = isInMaintenance(appMetadata)
  if (needsRedirect) {
    if (to.name !== 'maintenance') {
      const currentHost = window.location.host
      const domainsUnderMaintenance = getDomainUnderMaintenance(appMetadata)
      if (
        domainsUnderMaintenance.length === 0 ||
        domainsUnderMaintenance.some((domain) => currentHost.includes(domain))
      ) {
        router.push({ name: 'maintenance' })
      }
    }
  }
}

const needsPasswordRequest = (to: RouteLocationNormalized): void => {
  const paramsTo = to.query
  const planId = String(paramsTo.planId)
  const sharedPasswordManager = SharedPlanPasswordManager.getInstance()
  const passwordIsRequired = sharedPasswordManager.isPasswordRequired()

  if (passwordIsRequired && sessionStorage.getItem(planId) === null) {
    router.push({
      name: 'shared-plan-password',
      query: to.query,
    })
  }
}

const isUrlForSharedPlan = (to: RouteLocationNormalized): boolean => {
  const params = to.query
  const isPathValid = to.path === '/plan' || to.path === '/insights'
  const sharedPlan = params.shared === 'true' && isPathValid
  return sharedPlan
}

const isSharedPlanByUrlParams = () => {
  const url = window.location.href
  const queryParams = new URLSearchParams(url)
  const sharedPlan = queryParams.get('shared')
  return sharedPlan === 'true'
}

const isPasswordActionNeeded = () => {
  const sharedPasswordManager = SharedPlanPasswordManager.getInstance()
  const isPasswordRequired = sharedPasswordManager.isPasswordRequired()
  let isPasswordSet = false
  try {
    const password = sharedPasswordManager.getPassword()
    isPasswordSet = password !== undefined
  } catch (error) {
    // console.debug('🟨🟨🟨 Router: No password available')
    isPasswordSet = false
  }
  return isPasswordRequired && !isPasswordSet
}

const isSharedPlanActive = () => {
  const sharedPasswordManager = SharedPlanPasswordManager.getInstance()
  const isActive = sharedPasswordManager.isActive()

  return isActive
}

router.beforeEach((to, from) => {
  redirectToMaintenance(to)
  setValueToStorage(
    StorageDestination.SessionStorage,
    SessionStorageKeys.PreviousRoute,
    JSON.stringify(from.path),
  )
  const sharedPlan = isUrlForSharedPlan(to)
  const auth0Service = getAuth0ServiceIntance()
  let needsAuth = !to.matched.some((m) => m.meta.skipAuth)
  if (sharedPlan) {
    needsAuth = false
    if (isSharedPlanActive()) {
      if (isPasswordActionNeeded()) {
        // needsPasswordRequest() pushes to the password route as a side effect;
        // fall through to allow the current navigation (legacy behaviour).
        needsPasswordRequest(to)
      } else {
        return true
      }
    } else {
      const origin = window.location.origin
      // vue-router: redirect from a guard by returning the target location.
      return {
        name: 'shared-plan-feedback',
        query: { task: 'error', link: origin + to.fullPath },
      }
    }
  }
  const hasAuth = auth0Service?.isUserAuthenticated || authService.isLoggedIn()
  const scopes = authService.getScopes() as string[]
  const isSharedPlan = isSharedPlanByUrlParams()
  const redirection = { name: 'login', query: { ...to.query, next: to.path } }

  const needsToReAuthenticate = needsAuth && !hasAuth
  if (needsToReAuthenticate) {
    setValueToStorage(
      StorageDestination.LocalStorage,
      SessionStorageKeys.PlanUrl,
      to.path,
    )
    return '/login?next=' + to.path
  } else if (needsAuth) {
    if (hasAuth) {
      if (isSharedPlan) {
        const id = String(to.params.planid || to.params.id || to.params.planId)
        const type = to.params.type
        const allowedPlanIds = scopes
          .map((s) => s.split(':'))
          .filter((s) => s[0] === 'plan')
          .map((s) => s[1])

        const validPlanId = allowedPlanIds.indexOf(id) !== -1
        let allowed = false
        if (to.name === 'public') {
          allowed = true
        }
        if (to.name === 'plan' && validPlanId) {
          allowed = true
        }
        if (to.name === 'panel-insights') {
          if (type === 'panel') {
            allowed = true
          }
          if (type === 'plan') {
            allowed = validPlanId
          }
        }

        return allowed ? true : redirection
      } else {
        const urlOfPlanToredirect = getValueFromStorage(
          StorageDestination.LocalStorage,
          SessionStorageKeys.PlanUrl,
        )
        if (urlOfPlanToredirect) {
          removeValueFromStorage(
            StorageDestination.LocalStorage,
            SessionStorageKeys.PlanUrl,
          )
          return urlOfPlanToredirect
        }
        return true
      }
    } else {
      return redirection
    }
  }
  return true
})

export default router
